import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SyncLoader } from "react-spinners";
import ReactMarkdown from "react-markdown";
import { ResourceInterface } from "../../domain/interface/resource_interface";
import ThemeConstants from "../../../../config/theme/theme_constants";
import PlayIcon from "../../../../assets/images/play_icon.svg?react";
import PauseIcon from "../../../../assets/images/pause_icon.svg?react";
import SoundIcon from "../../../../assets/images/sound_icon.svg?react";
import MuteIcon from "../../../../assets/images/mute_icon.svg?react";
import ArrowBackIcon from "../../../../assets/images/arrow_back_icon.svg?react";
import { markdownComponentsDark } from "./markdown_components";
import ResourceFavoriteButton from "./resource_favorite_button";
import { useTranslation } from "../../../../i18n/useTranslation";

interface Props {
  resource: ResourceInterface;
  onCompleted: (id: string) => void;
}

const formatTime = (s: number): string => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const WAVEFORM_BARS = 32;

const SecureAudioPlayer: React.FC<Props> = ({ resource, onCompleted }) => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const navigate = useNavigate();
  const isUnlocked = resource.viewed === true;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const maxTimeRef = useRef(isUnlocked ? Number.POSITIVE_INFINITY : 0);
  const hasCompletedRef = useRef(false);
  const cheatTimeoutRef = useRef<number | null>(null);
  const recoverCountRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [cheatNotice, setCheatNotice] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [bufferedRanges, setBufferedRanges] = useState<[number, number][]>([]);
  const [isSeeking, setIsSeeking] = useState(false);

  const bars = useMemo(
    () =>
      Array.from({ length: WAVEFORM_BARS }, (_, i) => ({
        height: 20 + ((i * 13) % 60),
        delay: (i * 37) % 600,
        duration: 600 + ((i * 53) % 600),
      })),
    []
  );

  const handleCheat = useCallback((reason: string) => {
    const a = audioRef.current;
    if (a) a.pause();
    setCheatNotice(reason);
    if (cheatTimeoutRef.current) window.clearTimeout(cheatTimeoutRef.current);
    cheatTimeoutRef.current = window.setTimeout(() => setCheatNotice(null), 2000);
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    const onTimeUpdate = () => {
      if (!isUnlocked) {
        maxTimeRef.current = Math.max(maxTimeRef.current, a.currentTime);
      }
      setCurrentTime(a.currentTime);
    };
    const onSeeking = () => {
      setIsSeeking(true);
      if (isUnlocked) return;
      if (a.currentTime > maxTimeRef.current + 0.5) {
        a.currentTime = maxTimeRef.current;
        handleCheat(t('course.player.cheat'));
      }
    };
    const onRateChange = () => {
      if (isUnlocked) return;
      if (a.playbackRate > 1) {
        a.playbackRate = 1;
        handleCheat(t('course.player.speed_blocked'));
      }
    };
    const onLoadedMetadata = () => {
      setDuration(a.duration);
      setIsBuffering(false);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setIsSeeking(false);
    };
    const onEnded = () => {
      if (isUnlocked || hasCompletedRef.current) return;
      const tolerance = a.duration < 1 ? a.duration * 0.05 : 0.5;
      if (maxTimeRef.current >= a.duration - tolerance) {
        hasCompletedRef.current = true;
        onCompleted(resource._id);
      }
    };
    const onErr = () => {
      const err = a.error;
      // seek en arrière vers une zone évincée du buffer -> erreur réseau récupérable
      if (err && err.code === MediaError.MEDIA_ERR_NETWORK && recoverCountRef.current < 2) {
        recoverCountRef.current += 1;
        const resumeAt = a.currentTime;
        const wasPlaying = !a.paused;
        setIsBuffering(true);
        const restore = () => {
          a.removeEventListener("loadedmetadata", restore);
          try { a.currentTime = resumeAt; } catch { /* noop */ }
          if (wasPlaying) a.play().catch(() => {});
        };
        a.addEventListener("loadedmetadata", restore);
        a.load();
        return;
      }
      setHasError(true);
    };
    const onWaiting = () => setIsBuffering(true);
    const onLoadStart = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onPlaying = () => {
      setIsBuffering(false);
      setIsSeeking(false);
      recoverCountRef.current = 0;
    };
    const onProgress = () => {
      const ranges: [number, number][] = [];
      for (let i = 0; i < a.buffered.length; i++) ranges.push([a.buffered.start(i), a.buffered.end(i)]);
      setBufferedRanges(ranges);
    };
    const onVisibility = () => {
      if (isUnlocked) return;
      if (document.hidden) a.pause();
    };

    a.addEventListener("timeupdate", onTimeUpdate);
    a.addEventListener("seeking", onSeeking);
    a.addEventListener("ratechange", onRateChange);
    a.addEventListener("loadedmetadata", onLoadedMetadata);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("error", onErr);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("loadstart", onLoadStart);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("progress", onProgress);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      a.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("seeking", onSeeking);
      a.removeEventListener("ratechange", onRateChange);
      a.removeEventListener("loadedmetadata", onLoadedMetadata);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("error", onErr);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("loadstart", onLoadStart);
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("progress", onProgress);
      document.removeEventListener("visibilitychange", onVisibility);
      if (cheatTimeoutRef.current) window.clearTimeout(cheatTimeoutRef.current);
    };
  }, [resource._id, onCompleted, handleCheat, isUnlocked, t]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, []);

  const seekFromPointer = useCallback((clientX: number) => {
    const bar = barRef.current;
    const a = audioRef.current;
    if (!bar || !a || !duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const target = pct * duration;
    a.currentTime = isUnlocked ? target : Math.min(target, maxTimeRef.current);
  }, [duration, isUnlocked]);

  const onBarPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    seekFromPointer(e.clientX);
    const onMove = (ev: PointerEvent) => seekFromPointer(ev.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [seekFromPointer]);

  const handleVolumeChange = (val: number) => {
    setVolume(val);
    setIsMuted(val === 0);
    if (audioRef.current) {
      audioRef.current.volume = val;
      audioRef.current.muted = val === 0;
    }
  };

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (audioRef.current) audioRef.current.muted = next;
  };

  const maxPct = duration ? (maxTimeRef.current / duration) * 100 : 0;
  const curPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`fixed inset-0 z-50 overflow-hidden ${isUnlocked ? "" : "select-none"}`}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        backgroundImage: resource.picture ? `url(${resource.picture})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <style>{`
        @keyframes uvibes-wave {
          0% { transform: scaleY(0.35); }
          100% { transform: scaleY(1); }
        }
      `}</style>

      <div className="absolute inset-0 backdrop-blur-2xl bg-black/60" />

      <audio ref={audioRef} src={resource.content} preload="metadata" controlsList="nodownload noremoteplayback" className="hidden" />

      <button
        className="absolute top-[5.6dvh] left-[3%] cursor-pointer w-8 h-8 flex items-center justify-center text-white z-10"
        onClick={() => navigate(-1)}
        aria-label={t('common.button.back')}
      >
        <ArrowBackIcon className="w-5 h-auto"/>
      </button>

      <div className="absolute top-[5.6dvh] right-[3%] z-10">
        <ResourceFavoriteButton
          resourceId={resource._id}
          initialFavoriteId={resource._favoriteId}
          outlineColor="#FFFFFF"
        />
      </div>

      <div className="relative h-full w-full flex flex-col text-white">
        <div className="flex-1 overflow-y-auto px-6 pt-[8dvh] pb-6 flex flex-col items-center gap-8">
          <h1 className="text-3xl md:text-4xl font-bold text-center leading-tight max-w-2xl">
            {resource.title}
          </h1>

          <div className="h-32 shrink-0 w-full max-w-md flex items-center justify-center">
            {isBuffering && !isSeeking ? (
              <SyncLoader color="#ffffff" size={12} speedMultiplier={0.7} />
            ) : (
              <div className="flex items-end justify-center gap-1 sm:gap-1.5 h-full w-full px-2" aria-hidden>
                {bars.map((b, i) => (
                  <div
                    key={i}
                    className="flex-1 min-w-0 max-w-2 rounded-full bg-white/90 origin-bottom"
                    style={{
                      height: `${b.height}px`,
                      animation: `uvibes-wave ${b.duration}ms ${b.delay}ms infinite alternate ease-in-out`,
                      animationPlayState: isPlaying ? "running" : "paused",
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {hasError && (
            <span className="text-sm text-white/80">{t('course.player.audio_error')}</span>
          )}

          {cheatNotice && (
            <div
              className="px-3 py-1.5 rounded-full text-white text-xs font-semibold shadow-md"
              style={{ background: theme.UvibesPink }}
            >
              {cheatNotice}
            </div>
          )}

          {resource.description && (
            <div className="prose prose-invert prose-lg max-w-2xl text-white/90">
              <ReactMarkdown components={markdownComponentsDark}>
                {resource.description}
              </ReactMarkdown>
            </div>
          )}
        </div>

        <div className="shrink-0 w-full px-6 pb-[5dvh] pt-4 bg-gradient-to from-black/80 to-transparent flex flex-col items-center gap-4">
         <div className="w-full max-w-xl flex flex-col gap-4">
          <div
            ref={barRef}
            className="relative h-2 w-full rounded-full bg-white/25 cursor-pointer"
            onPointerDown={onBarPointerDown}
          >
            {duration > 0 && bufferedRanges.map(([s, e], i) => (
              <div
                key={i}
                className="absolute top-0 h-full rounded-full bg-white/40"
                style={{ left: `${(s / duration) * 100}%`, width: `${((e - s) / duration) * 100}%` }}
              />
            ))}
            {!isUnlocked && (
              <div
                className="absolute top-0 left-0 h-full rounded-full bg-white/50"
                style={{ width: `${maxPct}%` }}
              />
            )}
            <div
              className="absolute top-0 left-0 h-full rounded-full"
              style={{ width: `${curPct}%`, background: theme.UvibesPink }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow"
              style={{ left: `calc(${curPct}% - 6px)` }}
            />
          </div>

          <div className="flex items-center justify-between text-xs tabular-nums">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>

          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2 w-32">
              <button onClick={toggleMute} className="cursor-pointer w-6 h-6 flex items-center justify-center" aria-label={isMuted ? t('course.player.unmute') : t('course.player.mute')}>
                {isMuted || volume === 0 ? <MuteIcon className="w-5 h-5" /> : <SoundIcon className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="w-20 accent-white"
              />
            </div>

            <button
              onClick={togglePlay}
              disabled={isBuffering}
              className="cursor-pointer w-16 h-16 rounded-full flex items-center justify-center shadow-lg disabled:opacity-70 disabled:cursor-default"
              style={{ background: theme.UvibesPink }}
              aria-label={isPlaying ? t('course.player.pause') : t('course.player.play')}
            >
              {isPlaying ? (
                <PauseIcon className="w-7 h-7 text-white" />
              ) : (
                <PlayIcon className="w-7 h-7 text-white" />
              )}
            </button>

            <div className="w-32" />
          </div>
         </div>
        </div>
      </div>
    </div>
  );
};

export default SecureAudioPlayer;
